import React, { useEffect, useState } from "react";
import { openBillingPortal, pauseSubscription, cancelSubscription, fetchEntitlements } from "@/lib/api";
import { X, Pause, Loader2, Sparkles, CreditCard, XCircle } from "lucide-react";
import { toast } from "sonner";

/**
 * Cancellation save-flow. Shown when a subscriber clicks "Manage".
 * Offers Pause (grandfathered pre-2027 only, up to 60 days) →
 * 7-day self-serve cancel → portal for everything else.
 */
export default function ManageSubscriptionModal({ onClose, onPaused, onCancelled }) {
  const [busy, setBusy] = useState(null); // "pause" | "portal" | "cancel"
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [entitlements, setEntitlements] = useState(null);

  useEffect(() => {
    fetchEntitlements().then(setEntitlements).catch(() => setEntitlements(null));
  }, []);

  // Pause = pre-2027 signups only (server also enforces this in POST /api/payments/pause).
  // Detection heuristic: the umbra_monthly_promo lookup key was ONLY offered
  // to pre-2027 signups (promo window closes Dec 31 2026), so tier
  // === 'monthly_promo' is a reliable "grandfathered" signal without needing
  // subscription_started_at on the entitlements payload. Grandfathered lifetime
  // holders also qualify. Post-2027 monthly_standard users see no pause btn.
  const canPause = Boolean(
    entitlements &&
      !entitlements.listener_lifetime &&
      (entitlements.grandfathered_lifetime ||
        entitlements.tier === "monthly_promo")
  );

  const doPause = async () => {
    setBusy("pause");
    try {
      // 2 months request → server caps at 60 days
      const r = await pauseSubscription(2);
      toast.success(`Paused for ${r?.days || 60} days. We'll auto-resume then.`);
      if (onPaused) onPaused();
      onClose();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not pause the subscription.";
      toast.error(msg);
    } finally {
      setBusy(null);
    }
  };

  const doPortal = async () => {
    setBusy("portal");
    try {
      const url = await openBillingPortal();
      window.location.href = url;
    } catch (e) {
      toast.error("Could not open the billing portal.");
      setBusy(null);
    }
  };

  const doCancel = async () => {
    setBusy("cancel");
    try {
      const r = await cancelSubscription();
      const untilStr = r?.access_until
        ? new Date(r.access_until).toLocaleDateString(undefined, { month: "short", day: "numeric" })
        : "the end of your billing cycle";
      toast.success(`Cancellation scheduled. You keep access until ${untilStr}.`);
      if (onCancelled) onCancelled();
      onClose();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not schedule the cancellation.";
      toast.error(msg);
      setBusy(null);
      setConfirmCancel(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-obsidian-950/85 backdrop-blur-md flex items-center justify-center p-4"
      data-testid="manage-subscription-modal"
    >
      <div className="relative bg-obsidian-800 border border-obsidian-600 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          data-testid="manage-modal-close"
          className="absolute top-4 right-4 text-bone-500 hover:text-bone-200 transition-colors z-10"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>
        <div className="p-8 md:p-10">
          <p className="font-sans uppercase tracking-[0.28em] text-xs text-terracotta mb-4">
            Manage your subscription
          </p>
          <h2 className="font-serif text-3xl text-bone-100 leading-tight mb-4">
            Before you go — would a{" "}
            {canPause ? <em className="text-bone-300 italic">pause</em> : <em className="text-bone-300 italic">breather</em>}{" "}
            feel right?
          </h2>
          <p className="font-sans text-sm text-bone-400 leading-relaxed mb-8">
            {canPause
              ? "Shadow work moves in seasons. We can quietly pause your billing for up to 60 days — your entries and unlocks stay right where they are. On day 61 we auto-resume with the next charge."
              : "You can cancel within 7 days of your last charge and still keep access through the end of the current billing cycle. No refund, but no more charges either."}
          </p>

          <div className="space-y-3">
            {canPause && (
              <button
                onClick={doPause}
                disabled={busy !== null}
                data-testid="pause-60-days-btn"
                className="w-full flex items-center gap-4 border border-terracotta bg-terracotta/5 hover:bg-terracotta/15 p-5 text-left transition-colors disabled:opacity-60"
              >
                <div className="h-10 w-10 border border-terracotta text-terracotta flex items-center justify-center shrink-0">
                  {busy === "pause" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Pause className="h-4 w-4" />}
                </div>
                <div className="flex-1">
                  <p className="font-serif text-lg text-bone-100 mb-1">Pause billing · 60 days</p>
                  <p className="font-sans text-xs text-bone-400">
                    Grandfathered rate held. Auto-resumes on day 61 unless you cancel first.
                  </p>
                </div>
              </button>
            )}

            <button
              onClick={onClose}
              disabled={busy !== null}
              data-testid="keep-subscription-btn"
              className="w-full flex items-center gap-4 border border-obsidian-600 hover:border-bone-500 p-5 text-left transition-colors"
            >
              <div className="h-10 w-10 border border-obsidian-600 text-bone-300 flex items-center justify-center shrink-0">
                <Sparkles className="h-4 w-4" />
              </div>
              <div className="flex-1">
                <p className="font-serif text-lg text-bone-100 mb-1">Keep the workbook</p>
                <p className="font-sans text-xs text-bone-400">
                  Return to journaling. Nothing changes.
                </p>
              </div>
            </button>

            {!confirmCancel ? (
              <button
                onClick={() => setConfirmCancel(true)}
                disabled={busy !== null}
                data-testid="cancel-request-btn"
                className="w-full flex items-center gap-4 border border-obsidian-700 hover:border-obsidian-500 p-5 text-left transition-colors"
              >
                <div className="h-10 w-10 border border-obsidian-700 text-bone-400 flex items-center justify-center shrink-0">
                  <XCircle className="h-4 w-4" />
                </div>
                <div className="flex-1">
                  <p className="font-serif text-lg text-bone-200 mb-1">Cancel subscription</p>
                  <p className="font-sans text-xs text-bone-500">
                    Within 7 days of your last charge · access continues until the end of the cycle.
                  </p>
                </div>
              </button>
            ) : (
              <div
                data-testid="cancel-confirm-panel"
                className="border border-terracotta/50 bg-terracotta/5 p-5"
              >
                <p className="font-serif text-lg text-bone-100 mb-2">
                  Cancel at the end of your billing cycle?
                </p>
                <p className="font-sans text-xs text-bone-400 leading-relaxed mb-5">
                  Your subscription will end at the close of the current cycle. You keep full access
                  until then. No refund, no further charges. Your entries stay yours.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={doCancel}
                    disabled={busy !== null}
                    data-testid="cancel-confirm-btn"
                    className="flex-1 flex items-center justify-center gap-2 border border-terracotta bg-terracotta text-obsidian-900 hover:bg-terracotta-deep hover:text-bone-100 px-4 py-3 font-sans text-xs uppercase tracking-[0.2em] transition-colors disabled:opacity-60"
                  >
                    {busy === "cancel" ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
                    Yes, cancel
                  </button>
                  <button
                    onClick={() => setConfirmCancel(false)}
                    disabled={busy !== null}
                    data-testid="cancel-abort-btn"
                    className="flex-1 border border-obsidian-600 hover:border-bone-500 text-bone-300 px-4 py-3 font-sans text-xs uppercase tracking-[0.2em] transition-colors"
                  >
                    Never mind
                  </button>
                </div>
              </div>
            )}

            <button
              onClick={doPortal}
              disabled={busy !== null}
              data-testid="portal-btn"
              className="w-full flex items-center gap-4 border border-obsidian-700 hover:border-obsidian-500 p-4 text-left transition-colors"
            >
              <div className="h-9 w-9 border border-obsidian-700 text-bone-500 flex items-center justify-center shrink-0">
                {busy === "portal" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
              </div>
              <div className="flex-1">
                <p className="font-sans text-sm text-bone-200 mb-0.5">Update card · cancel after 7 days</p>
                <p className="font-sans text-[0.7rem] text-bone-500">
                  Opens the Stripe billing portal in a new tab.
                </p>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
